// Shared Waytide brand block: the logo icon (favicon.svg rendered as a
// mask so it picks up currentColor and tracks theme changes) plus the
// "Waytide" wordmark. Used in the TopBar, the code gate, and the sign-in
// page so every brand surface in the app is the same icon + text combo
// with identical click target (/ — the root redirects appropriately).

import { useNavigate } from "react-router-dom";

interface BrandLogoProps {
  /** Square size in px. Defaults to 20 for inline use. */
  size?: number;
  /** Extra classes (color, hover, etc.) applied to the mask div. */
  className?: string;
}

/** Pure logo mark. Fills with currentColor via CSS mask. */
export function BrandLogo({ size = 20, className = "" }: BrandLogoProps) {
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        backgroundColor: "currentColor",
        maskImage: "url(/favicon.svg)",
        maskSize: "contain",
        maskRepeat: "no-repeat",
        maskPosition: "center",
        WebkitMaskImage: "url(/favicon.svg)",
        WebkitMaskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
      }}
    />
  );
}

interface BrandMarkProps {
  /** "inline" for the top bar, "hero" for the centered sign-in / gate pages. */
  variant?: "inline" | "hero";
  /** Override the click target. Defaults to "/" (the root redirect). */
  href?: string;
}

/** Clickable logo + wordmark. Same icon, same text, same click target everywhere. */
export function BrandMark({ variant = "inline", href = "/" }: BrandMarkProps) {
  const navigate = useNavigate();
  const isHero = variant === "hero";
  const size = isHero ? 56 : 20;

  return (
    <button
      onClick={() => navigate(href)}
      aria-label="Home"
      title="Home"
      className={`flex cursor-pointer items-center text-accent transition-colors hover:text-primary ${
        isHero ? "flex-col gap-3" : "gap-2"
      }`}
      data-testid="brand-mark"
    >
      <BrandLogo size={size} />
      <span
        className={`text-primary ${
          isHero
            ? "text-5xl font-semibold tracking-[0.02em]"
            : "text-base font-semibold"
        }`}
      >
        Waytide
      </span>
    </button>
  );
}
